import get from 'lodash-es/get';
import { store } from './store.js';

// Read from the server-injected config, not Firestore's: the watchdog must run when Firestore is stuck.
const STUCK_TIMEOUT = Number(window.K.config.bootWatchdog_stuckTimeout) || 60000;

// Guards against a reload loop when a reload does not un-stick boot.
const RELOADED_AT_KEY = 'kerika-boot-reloaded-at';
const RELOAD_MIN_INTERVAL = Number(window.K.config.bootWatchdog_reloadMinInterval) || 5 * 60 * 1000;

// Auth fields the router waits for before it leaves the loader.
const AUTH_FIELDS = ['user', 'ownedAccounts', 'accessibleAccounts', 'lastAccessibleAccount', 'accessibleBoards'];

let stuckTimeout;
let options = {};

/**
 * Watches boot, and when the app is still on the loader after `STUCK_TIMEOUT` (or `reportStuckBoot` is
 * called), reports why to Sentry and reloads the page once. A fresh boot is what un-sticks it; users
 * otherwise had to kill the installed app.
 * @param {Object} bootOptions
 *  - @property {Function} isLoading returns true while the app is still on the loader.
 *  - @property {String} deviceId
 *  - @property {Function} getVersion returns the running app version.
 */
export const startBootWatchdog = (bootOptions) => {
  options = bootOptions;
  startStuckTimeout();
};

export const stopBootWatchdog = () => {
  window.clearTimeout(stuckTimeout);
  document.removeEventListener('visibilitychange', restartWhenVisible);
};

const startStuckTimeout = () => {
  stuckTimeout = window.setTimeout(onStuckTimeout, STUCK_TIMEOUT);
};

// Browsers throttle hidden tabs, so a slow boot there is not a stuck one; the timeout restarts once the tab is visible.
const onStuckTimeout = () => {
  if (document.visibilityState === 'hidden') {
    document.addEventListener('visibilitychange', restartWhenVisible);
    return;
  }
  // A headless browser's virtual clock fires the timeout early; real time since navigation has not reached it.
  if (performance.now() < STUCK_TIMEOUT) {
    return;
  }
  reportStuckBoot('LOADING_TIMEOUT');
};

const restartWhenVisible = () => {
  if (document.visibilityState !== 'visible') {
    return;
  }
  document.removeEventListener('visibilitychange', restartWhenVisible);
  startStuckTimeout();
};

/**
 * @param {String} reason `AUTH_INIT_FAILED` or `LOADING_TIMEOUT`
 * @param {Error} error
 */
export const reportStuckBoot = async (reason, error) => {
  stopBootWatchdog();
  // Headless crawlers run timers on virtual time, so the timeout fires within seconds of a boot that is not stuck.
  if (navigator.webdriver || !options.isLoading || !options.isLoading()) {
    return;
  }

  const registration = navigator.serviceWorker && await navigator.serviceWorker.getRegistration().catch(() => undefined);
  // Offline boots recover on their own once requests go through, so they are only reported.
  const shouldReload = navigator.onLine && Date.now() - lastReloadedAt() > RELOAD_MIN_INTERVAL;
  const details = {
    reason,
    error: error && String(error),
    pendingAuthFields: AUTH_FIELDS.filter((field) => get(store.getState(), `auth.${field}`) === undefined),
    hasWaitingServiceWorker: !!(registration && registration.waiting),
    version: (options.getVersion && options.getVersion()) || '',
    visibilityState: document.visibilityState,
    online: navigator.onLine,
    reloading: shouldReload,
    elapsed: Math.round(performance.now()),
  };
  console.error(`${options.deviceId}: App is stuck on the loader. ${JSON.stringify(details)}`);

  if (shouldReload) {
    reloadWhenVisible();
  }
};

const lastReloadedAt = () => {
  try {
    return Number(window.sessionStorage.getItem(RELOADED_AT_KEY)) || 0;
  } catch (error) {
    return 0;
  }
};

const reload = () => {
  if (!options.isLoading()) {
    return;
  }
  try {
    window.sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch (error) {
    // Without sessionStorage the reload still goes ahead.
  }
  window.location.reload();
};

const reloadWhenVisible = () => {
  if (document.visibilityState === 'visible') {
    reload();
    return;
  }

  const reloadOnVisible = () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    document.removeEventListener('visibilitychange', reloadOnVisible);
    reload();
  };
  document.addEventListener('visibilitychange', reloadOnVisible);
};
